import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, ChevronRight, Trash2, X, BookOpen, Layers } from 'lucide-react';
import { programsApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import type { TrainingProgram } from '@/types';

export function ProgramsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProgramName, setNewProgramName] = useState('');
  const [newProgramDescription, setNewProgramDescription] = useState('');

  // Hae ohjelmat
  const { data, isLoading, error } = useQuery({
    queryKey: ['programs'],
    queryFn: programsApi.list,
  });

  // Luo uusi ohjelma
  const createMutation = useMutation({
    mutationFn: programsApi.create,
    onSuccess: (program) => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      setShowCreateModal(false);
      setNewProgramName('');
      setNewProgramDescription('');
      // Siirry uuden ohjelman sivulle
      navigate(`/programs/${program.id}`);
    },
  });

  // Poista ohjelma
  const deleteMutation = useMutation({
    mutationFn: programsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      setDeleteConfirm(null);
    },
  });

  const handleDeleteClick = (id: string, e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteConfirm(id);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm) {
      await deleteMutation.mutateAsync(deleteConfirm);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm(null);
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newProgramName.trim()) {
      createMutation.mutate({
        name: newProgramName.trim(),
        description: newProgramDescription.trim() || undefined,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Virhe ladattaessa ohjelmia</p>
      </div>
    );
  }

  const programs = data?.programs || [];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Treeniohjelmat
          </h1>
          <p className="text-gray-500 mt-1">
            {programs.length} ohjelmaa
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          <Plus className="h-5 w-5" />
          <span className="hidden sm:inline">Uusi ohjelma</span>
        </button>
      </div>

      {/* Ohjelmalista */}
      {programs.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Ei vielä treeniohjelmia
          </h2>
          <p className="text-gray-500 mb-6">
            Luo ensimmäinen treeniohjelmasi ja lisää siihen treenipohjia!
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            <Plus className="h-5 w-5" />
            Luo ensimmäinen ohjelma
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {programs.map((program: TrainingProgram) => (
            <ProgramCard
              key={program.id}
              program={program}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      )}

      {/* Luo ohjelma -modaali */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Uusi treeniohjelma
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateSubmit}>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="program-name"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Ohjelman nimi *
                  </label>
                  <input
                    type="text"
                    id="program-name"
                    value={newProgramName}
                    onChange={(e) => setNewProgramName(e.target.value)}
                    placeholder="esim. 12 viikon voimaohjelma"
                    className="input w-full"
                    autoFocus
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="program-description"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Kuvaus (valinnainen)
                  </label>
                  <textarea
                    id="program-description"
                    value={newProgramDescription}
                    onChange={(e) => setNewProgramDescription(e.target.value)}
                    placeholder="Lyhyt kuvaus ohjelmasta..."
                    rows={3}
                    className="input w-full resize-none"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 btn btn-secondary"
                >
                  Peruuta
                </button>
                <button
                  type="submit"
                  disabled={!newProgramName.trim() || createMutation.isPending}
                  className="flex-1 btn btn-primary"
                >
                  {createMutation.isPending ? 'Luodaan...' : 'Luo ohjelma'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Poiston vahvistus -modaali */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleCancelDelete}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Poista ohjelma?
              </h3>
              <button
                onClick={handleCancelDelete}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Haluatko varmasti poistaa tämän ohjelman? Kaikki ohjelman treenipohjat poistetaan myös. Toimintoa ei voi perua.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelDelete}
                className="flex-1 btn btn-secondary"
              >
                Peruuta
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
                className="flex-1 btn bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteMutation.isPending ? 'Poistetaan...' : 'Poista'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Ohjelmakortti
function ProgramCard({
  program,
  onDelete,
}: {
  program: TrainingProgram;
  onDelete: (id: string, e: React.MouseEvent | React.TouchEvent) => void;
}) {
  const templateCount = program._count?.templates ?? program.templates?.length ?? 0;

  return (
    <Link
      to={`/programs/${program.id}`}
      className="card hover:shadow-md transition-shadow block"
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Nimi ja aktiivisuus */}
            <div className="flex items-center gap-2 mb-1">
              <BookOpen className="h-5 w-5 text-primary-500" />
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {program.name}
              </h3>
              {program.isActive && (
                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                  Aktiivinen
                </span>
              )}
            </div>

            {/* Kuvaus */}
            {program.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                {program.description}
              </p>
            )}

            {/* Pohjien määrä */}
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <Layers className="h-4 w-4" />
              <span>
                {templateCount} {templateCount === 1 ? 'treenipohja' : 'treenipohjaa'}
              </span>
            </div>
          </div>

          {/* Toiminnot */}
          <div className="flex items-center gap-2 ml-4">
            <button
              type="button"
              onClick={(e) => onDelete(program.id, e)}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(program.id, e);
              }}
              className="p-3 text-gray-400 hover:text-red-500 active:text-red-600 transition-colors touch-manipulation"
              title="Poista"
            >
              <Trash2 className="h-5 w-5" />
            </button>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </div>
      </div>
    </Link>
  );
}
